import React, { useState, useEffect, useRef } from 'react';
import { getQuestionAudioBlobUrl, transcribeAudio } from '../../lib/interviewApi';

export default function InterviewQuestion({ question, session, onSubmit, submitting }) {
  const [answer, setAnswer] = useState('');
  const [audioState, setAudioState] = useState('idle'); // idle | loading | playing | error
  // mic states: idle | recording | processing | error
  const [micState, setMicState] = useState('idle');
  const [micError, setMicError] = useState('');
  const audioRef = useRef(null);
  const blobUrlRef = useRef(null);
  const recorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const streamRef = useRef(null);
  const total = session?.questions?.length || 0;
  // current index is zero-based; display as 1-based
  const currentIndex = session?.current_question_index ?? 0;
  const displayNumber = currentIndex + 1;
  const progress = total > 0 ? Math.round((currentIndex / total) * 100) : 0;

  // Clear the text area + revoke any old blob URL when a new question arrives
  useEffect(() => {
    setAnswer('');
    setAudioState('idle');
    setMicState('idle');
    setMicError('');
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, [question?.question_id]);

  // Tear down any active mic stream on unmount.
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, []);

  const startRecording = async () => {
    setMicError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      recordedChunksRef.current = [];
      // Pick the best supported mime; Safari needs audio/mp4, Chrome/Firefox prefer webm.
      const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
      const mimeType = candidates.find((m) => MediaRecorder.isTypeSupported(m)) || '';
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        // Release the mic immediately so the OS indicator goes away.
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
        const blob = new Blob(recordedChunksRef.current, {
          type: recorder.mimeType || 'audio/webm',
        });
        if (!blob.size) {
          setMicError('No audio captured');
          setMicState('error');
          return;
        }
        setMicState('processing');
        try {
          const { transcript } = await transcribeAudio(
            session.interview_id,
            question.question_id,
            blob,
          );
          // Append to existing answer so the candidate can record multiple takes.
          setAnswer((prev) => (prev ? prev + '\n\n' : '') + (transcript || ''));
          setMicState('idle');
        } catch (e) {
          setMicError(e.message || 'Transcription failed');
          setMicState('error');
        }
      };
      recorderRef.current = recorder;
      recorder.start();
      setMicState('recording');
    } catch (e) {
      setMicError(e.name === 'NotAllowedError' ? 'Microphone permission denied' : e.message);
      setMicState('error');
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
  };

  const playAudio = async () => {
    if (!session?.interview_id || !question?.question_id) return;
    if (audioRef.current && blobUrlRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
      return;
    }
    setAudioState('loading');
    const url = await getQuestionAudioBlobUrl(session.interview_id, question.question_id);
    if (!url) {
      setAudioState('error');
      return;
    }
    blobUrlRef.current = url;
    const audio = new Audio(url);
    audio.onended = () => setAudioState('idle');
    audio.onerror = () => setAudioState('error');
    audio.onplaying = () => setAudioState('playing');
    audioRef.current = audio;
    audio.play().catch(() => setAudioState('error'));
  };

  const handleSubmit = () => {
    if (!answer.trim() || submitting) return;
    onSubmit(answer);
  };

  return (
    <div className="max-w-2xl mx-auto p-8 bg-[#fdfaf0] rounded-xl border border-[#1b5e20]/20 shadow-sm">
      <div className="mb-4">
        <div className="flex justify-between text-sm text-gray-600 mb-1">
          <span>Question {displayNumber} of {total}</span>
          <span className="uppercase text-xs tracking-wide text-[#b8860b] font-semibold">
            {question.category}
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-[#1b5e20] transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="flex items-start gap-3 mb-6">
        <button
          type="button"
          onClick={playAudio}
          aria-label={audioState === 'playing' ? 'Replay question audio' : 'Play question audio'}
          className="flex-shrink-0 mt-1 w-10 h-10 rounded-full bg-[#1b5e20] hover:bg-[#2e7d32] text-white flex items-center justify-center transition disabled:opacity-50"
          disabled={audioState === 'loading'}
          title={
            audioState === 'error'
              ? 'Audio unavailable - read the text below'
              : audioState === 'playing'
              ? 'Playing...'
              : 'Listen to the question'
          }
        >
          {audioState === 'loading' ? (
            <span className="block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : audioState === 'playing' ? (
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" /><rect x="14" y="5" width="4" height="14" /></svg>
          ) : (
            <svg className="w-5 h-5 ml-0.5" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
          )}
        </button>
        <h2 className="text-xl sm:text-2xl font-semibold text-[#1b5e20]">
          {question.text}
        </h2>
      </div>

      <textarea
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        placeholder="Type your answer here, or use the mic to dictate..."
        rows={8}
        disabled={submitting || micState === 'recording' || micState === 'processing'}
        className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:border-[#1b5e20] focus:ring-2 focus:ring-[#1b5e20]/20 disabled:bg-gray-100"
      />

      <div className="mt-2 flex items-center gap-3">
        <button
          type="button"
          onClick={micState === 'recording' ? stopRecording : startRecording}
          disabled={submitting || micState === 'processing'}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition ${
            micState === 'recording'
              ? 'bg-red-600 text-white hover:bg-red-700 animate-pulse'
              : 'bg-white border border-[#1b5e20] text-[#1b5e20] hover:bg-[#1b5e20]/5'
          } disabled:opacity-50`}
          aria-label={micState === 'recording' ? 'Stop recording' : 'Record answer'}
        >
          {micState === 'recording' ? (
            <>
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="1.5" /></svg>
              Stop ({/* live indicator */}<span className="inline-block w-2 h-2 rounded-full bg-white animate-pulse ml-1" />)
            </>
          ) : micState === 'processing' ? (
            <>
              <span className="block w-4 h-4 border-2 border-[#1b5e20] border-t-transparent rounded-full animate-spin" />
              Transcribing...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3zm5 9a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2z" /></svg>
              Record answer
            </>
          )}
        </button>
        {micError && (
          <span className="text-xs text-red-600">{micError}</span>
        )}
      </div>

      <button
        onClick={handleSubmit}
        disabled={!answer.trim() || submitting || micState === 'recording' || micState === 'processing'}
        className="mt-4 w-full py-3 bg-[#1b5e20] text-white font-semibold rounded-lg hover:bg-[#2e7d32] transition disabled:bg-gray-400 disabled:cursor-not-allowed"
      >
        {submitting ? 'Evaluating...' : 'Next Question'}
      </button>
    </div>
  );
}
